import { Icon } from '@cfa/react-components';
import { Check } from '@cfa/system-icons';
import Fuse from 'fuse.js';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { ascend, prop, propOr, sortWith } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';

import { actions as userActions } from '../../user/reducer';
import {
  isAdminUser,
  isVcaUser,
  selectAllStoreNamesAndNumbers,
  selectLocationDropdownDisabled,
  selectLocationNumber,
  selectUserLocations,
} from '../../user/selectors';

import down from '../../assets/down.svg';
import up from '../../assets/up.svg';
import ooeConstants from '../../constants';
import { actions as orderActions } from '../../order/reducers';
import * as visitedLocationsManager from '../../services/visitedLocationsManager';
import type { LocationResult } from '../../user/schemas';

type SearchableLocation = { name: string; locationNumber: string };

const CheckIcon = <Icon className="checkIcon" color="primary" icon={Check} size="sm" />;

export const UserLocation: React.FC = () => {
  const dispatch = useDispatch();
  const locations = useSelector(selectUserLocations);
  const selectedLocation = useSelector(selectLocationNumber);
  const locationDropdownDisabled = useSelector(selectLocationDropdownDisabled);
  const allStoreNamesAndNumbers = useSelector(selectAllStoreNamesAndNumbers);
  const isAdmin = useSelector(isAdminUser);
  const isVca = useSelector(isVcaUser);

  const [searchTerms, setSearchTerms] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(true);
  const dropdownClassName = showLocationDropdown ? 'location-dropdown expand' : 'location-dropdown';
  const topLocations = visitedLocationsManager.getTopLocations(ooeConstants.MAX_NUM_OF_DISPLAYED_LOCATIONS);

  const currentLocation: LocationResult | undefined = locations.filter(
    ({ locationNumber }) => locationNumber === selectedLocation,
  )[0];

  const ldClient = useLDClient();

  const fuseOptions = {
    keys: ['name', 'locationNumber'],
    threshold: 0.3,
    ignoreLocation: true,
    minMatchCharLength: 2,
    shouldSort: true,
    findAllMatches: false,
    distance: 100,
  };

  useEffect(() => {
    if (showLocationDropdown && locationDropdownDisabled) {
      setShowLocationDropdown(false);
    }
  }, [locationDropdownDisabled]);

  useEffect(() => {
    if (currentLocation) {
      const ldContext = ldClient?.getContext() ?? {};
      ldClient?.identify({
        ...ldContext,
        kind: 'multi',
        location: {
          key: currentLocation.locationNumber,
          state: currentLocation.locationContact.streetAddress.state,
        },
      });
    }
  }, [currentLocation]);

  const sort: (arr: SearchableLocation[]) => SearchableLocation[] = sortWith([ascend(prop('name'))]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const s = e.target.value;
    setSearchTerms(s.trimStart());
  };

  const onUnderMaskClickHandler = () => {
    setShowLocationDropdown(false);
  };

  const getCurrentLocation = () => {
    return `${propOr('', 'name', currentLocation)}`;
  };

  const toggleLocationDropdown = () => {
    setShowLocationDropdown((prev) => !prev);
  };

  const handleUpdateLocation = (locationNumber: string, name: string) => {
    if (selectedLocation === locationNumber) {
      setShowLocationDropdown(false);
      return;
    }
    dispatch(orderActions.clearAddressFields());
    dispatch(userActions.updateUserLocation({ locationNumber }));
    setSearchTerms('');
    setShowLocationDropdown(false);

    visitedLocationsManager.updateLocation(locationNumber, name);

    dispatch(orderActions.setDetailsDate(null));
    dispatch(orderActions.setDetailsTime(''));
  };

  const renderLocationDropdown = () => (
    <div className={dropdownClassName}>
      <input
        className="locations-search"
        data-cy="location-search"
        onChange={handleSearchInputChange}
        placeholder="Search Stores"
        value={searchTerms}
      />
      <ul className="locations-list">
        {matchedLocations.map((location) => (
          <li key={location.locationNumber}>
            <button
              className={location.locationNumber === selectedLocation ? 'selected-location' : ''}
              data-cy={location.locationNumber}
              onClick={() => handleUpdateLocation(location.locationNumber, location.name)}
              value={location.locationNumber}
            >
              <div className="flex">
                {`${location.name} ${location.locationNumber}`}
                {location.locationNumber === selectedLocation ? CheckIcon : ''}
              </div>
            </button>
          </li>
        ))}
        {topLocations.length > 0 && <li className="divider">Frequently used locations:</li>}
        {topLocations.map((location) => (
          <li key={location.locationNumber}>
            <button
              className={location.locationNumber === selectedLocation ? 'selected-location' : ''}
              data-cy={location.locationNumber}
              onClick={() => handleUpdateLocation(location.locationNumber, location.name)}
              value={location.locationNumber}
            >
              <div className="flex">
                {`${location.name} ${location.locationNumber}`}
                {location.locationNumber === selectedLocation ? CheckIcon : ''}
              </div>
            </button>
          </li>
        ))}
      </ul>
      <div className="under-mask" onClick={onUnderMaskClickHandler} role="presentation" />
    </div>
  );

  const dropdownArrow = () => {
    if (!locationDropdownDisabled) {
      const arrowSrc = showLocationDropdown ? up : down;
      return <img alt="arrow" className="arrow" src={arrowSrc} />;
    }
    return null;
  };

  const storesToSearch = useMemo(() => {
    return isAdmin || isVca
      ? allStoreNamesAndNumbers
      : allStoreNamesAndNumbers.filter((store) =>
          locations.some((loc) => loc.locationNumber === store.locationNumber),
        );
  }, [isAdmin, isVca, allStoreNamesAndNumbers, locations]);

  const determineSearchData = () => {
    return storesToSearch.length > 0 && searchTerms.length > 0 ? storesToSearch : locations;
  };

  const performSingleTokenSearch = () => {
    return fuse.search(searchTerms.trim()).map((result) => result.item);
  };

  const performMultiTokenSearch = (tokens: string[]) => {
    const filteredResults = searchData.filter((item) =>
      tokens.every((token) => {
        const lowerToken = token.toLowerCase();
        return item.name.toLowerCase().includes(lowerToken) || item.locationNumber.includes(token);
      }),
    );
    return sort(filteredResults);
  };

  const performLocationSearch = () => {
    if (searchTerms.length === 0) {
      return sort(searchData);
    }

    // split searchTerms string into an array of non-empty tokens using whitespace as delimiter
    const tokens = searchTerms.trim().split(/\s+/).filter(Boolean);
    return tokens.length === 1 ? performSingleTokenSearch() : performMultiTokenSearch(tokens);
  };

  const searchData = determineSearchData();
  const fuse = useMemo(() => new Fuse(searchData, fuseOptions), [searchData, fuseOptions]);
  const matchedLocations = performLocationSearch().slice(0, ooeConstants.MAX_NUM_OF_DISPLAYED_LOCATIONS);

  return (
    <StyledUserLocation blurBackground={showLocationDropdown} usePointerForCursor={!locationDropdownDisabled}>
      <button
        className="selected-location"
        data-cy="selected-location"
        disabled={locationDropdownDisabled}
        onClick={toggleLocationDropdown}
      >
        {getCurrentLocation()}
        {dropdownArrow()}
      </button>
      {renderLocationDropdown()}
    </StyledUserLocation>
  );
};

const slideDown = keyframes`
  0% { transform: translateY(-100%); visibility: visible; }
  100% { transform: none; }
`;

const slideUp = keyframes`
  0% { transform: none; visibility: visible; }
  100% { transform: translateY(-100%); visibility: hidden; }
`;

const StyledUserLocation = styled.div<{ usePointerForCursor: boolean; blurBackground: boolean }>`
  & .selected-location {
    font: ${(props) => props.theme.regularBoldFont};
    color: ${(props) => props.theme.colors.primary};
    background-color: ${(props) => props.theme.colors.background};
    cursor: ${(props) => props.usePointerForCursor && 'pointer'};
    border: none;
    text-align: right;
  }

  & .location-dropdown {
    position: fixed;
    top: -100%;
    right: 0;
    display: flex;
    flex-direction: column;
    width: 270px;
    will-change: transform;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;
    animation-duration: 200ms;
    animation-name: ${slideUp};
    z-index: 2;
  }

  .location-dropdown.expand {
    top: 70px;
    animation-name: ${slideDown};
  }

  & .locations-search {
    font: ${(props) => props.theme.regularTextFont};
    color: ${(props) => props.theme.colors.text};
    background-color: white;
    border: none;
    border-bottom: 2px solid ${(props) => props.theme.colors.primary};
    height: 40px;
    padding: 10px 20px;
  }

  & .locations-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
    background-color: white;
  }

  & .locations-list button {
    font: ${(props) => props.theme.regularTextFont};
    color: ${(props) => props.theme.colors.text};
    background-color: white;
    cursor: pointer;
    border: none;
    text-align: left;
    padding-left: 20px;
    height: 38px;
    width: 100%;
    margin: 4px 0;

    &:hover {
      background-color: #f2f6f8;
    }
  }

  & .locations-list button.selected-location {
    background-color: ${(props) => props.theme.colors.readonly};

    &:hover {
      background-color: #b0c8d3;
    }
  }

  & .divider {
    background-color: white;
    text-align: left;
    padding: 8px 12px 4px;
    color: ${(props) => props.theme.colors.primary};
    border-top: solid 2px ${(props) => props.theme.colors.grayscale.gray2};
  }

  & .count {
    font: ${(props) => props.theme.smallTextFont};
    background-color: ${(props) => props.theme.colors.background};
    padding: 10px;
    text-align: right;
  }

  & .arrow {
    width: 8px;
    margin-left: 5px;
    margin-bottom: 1px;
  }

  & .flex {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  & .under-mask {
    position: fixed;
    width: 100vw;
    height: 100vh;
    right: 0;
    z-index: -1;
    background-color: ${(props) => props.blurBackground && '#c1c1c1a6'};
  }

  @media (max-width: ${(props) => props.theme.phone}) {
    & .selected-location {
      font: ${(props) => props.theme.smallTextFont};
      width: 100vw;
      text-align: center;
      margin-top: 43px;
    }

    & .location-dropdown {
      position: fixed;
      left: 0;
      width: 100%;
    }
  }
`;

export default UserLocation;
